import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { SextforceAutoRepostAdjustUnit, SextforceAutoRepostWithLatestRepostLog } from '../../../../hooks/useSextforceAutoRepost';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    autoReposts: SextforceAutoRepostWithLatestRepostLog[];
    refetchAutoReposts: () => void;
    bulkAdjustAutoReposts: (
        autoRepostIds: string[],
        offsetAmount: number,
        offsetUnit: SextforceAutoRepostAdjustUnit,
    ) => Promise<{ success: boolean; adjustedCount?: number }>;
    bulkAdjustAutoRepostsLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'repostCount' | 'nextRepostDate';
type SortDirection = 'asc' | 'desc';
type AdjustDirection = 'later' | 'earlier';

const dateFormat = 'DD/MM/YYYY hh:mm A';

const SextforceAutoRepostBulkAdjustDialog = (props: Props) => {
    const { autoReposts, refetchAutoReposts, bulkAdjustAutoReposts, bulkAdjustAutoRepostsLoading, open, setOpen } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedAutoRepostIds, setSelectedAutoRepostIds] = useState<string[]>([]);
    const [direction, setDirection] = useState<AdjustDirection>('later');
    const [offsetAmount, setOffsetAmount] = useState<number>(1);
    const [offsetUnit, setOffsetUnit] = useState<SextforceAutoRepostAdjustUnit>('hours');

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('nextRepostDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Signed offset sent to the server: negative moves earlier
    const signedOffset = direction === 'later' ? offsetAmount : -offsetAmount;

    const shiftDate = (date: Date | string): moment.Moment => moment(date).add(signedOffset, offsetUnit);

    // Sort the auto reposts based on current sort settings
    const sortedAutoReposts = useMemo(() => {
        return [...autoReposts].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'repostCount':
                    aValue = a.repostCount;
                    bValue = b.repostCount;
                    break;
                case 'nextRepostDate':
                    aValue = new Date(a.nextRepostDate).getTime();
                    bValue = new Date(b.nextRepostDate).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [autoReposts, sortField, sortDirection]);

    const selectedAutoReposts = useMemo(
        () => autoReposts.filter(autoRepost => autoRepost._id && selectedAutoRepostIds.includes(autoRepost._id)),
        [autoReposts, selectedAutoRepostIds],
    );

    // Selected items whose new time would already be in the past (they would repost at the next scheduler run)
    const now = moment();
    const movedIntoPastCount = selectedAutoReposts.filter(autoRepost => shiftDate(autoRepost.nextRepostDate).isBefore(now)).length;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selection and inputs when the dialog is closed
        setSelectedAutoRepostIds([]);
        setDirection('later');
        setOffsetAmount(1);
        setOffsetUnit('hours');
        setOpen(false);
    };

    const offsetDescription = `${offsetAmount.toLocaleString()} ${offsetAmount === 1 ? offsetUnit.slice(0, -1) : offsetUnit} ${direction}`;

    // Handle adjust action
    const handleAdjust = () => {
        if (offsetAmount < 1 || selectedAutoRepostIds.length === 0) {
            return;
        }

        dialog
            .confirm({
                title: 'Adjust Auto Reposts',
                message: `Move the next repost of ${selectedAutoRepostIds.length.toLocaleString()} Auto Reposts ${offsetDescription}?${
                    movedIntoPastCount > 0
                        ? ` ${movedIntoPastCount.toLocaleString()} of them will end up in the past and will repost at the next scheduler run.`
                        : ''
                }`,
                ok: {
                    text: 'Adjust',
                    color: 'primary',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                bulkAdjustAutoReposts(selectedAutoRepostIds, signedOffset, offsetUnit)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(`Auto Reposts moved ${offsetDescription}`, {
                                variant: 'success',
                            });

                            refetchAutoReposts();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to adjust Auto Reposts', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to adjust Auto Reposts', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    const sortLabelSx = {
        fontWeight: 'bold',
        '& .MuiTableSortLabel-icon': {
            color: 'inherit !important',
        },
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Auto Reposts Bulk Adjust`} />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Auto Reposts whose next repost time you want to move. Every selected Auto Repost will be moved by the
                        same amount, keeping the gaps between them. Useful after the clocks change. You can select all Auto Reposts by
                        checking the checkbox in the header.
                    </Typography>

                    <Box sx={{ pl: 2, pr: 2 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Move the selected Auto Reposts...
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="By..."
                                    type="number"
                                    size="small"
                                    value={offsetAmount}
                                    onChange={e => setOffsetAmount(Math.max(1, parseInt(e.target.value, 10) || 0))}
                                    fullWidth
                                    InputProps={{
                                        inputProps: { min: 1 },
                                    }}
                                    error={offsetAmount < 1}
                                />

                                <Select
                                    value={offsetUnit}
                                    size="small"
                                    onChange={e => setOffsetUnit(e.target.value as SextforceAutoRepostAdjustUnit)}
                                    fullWidth
                                >
                                    <MenuItem value="minutes">Minutes</MenuItem>
                                    <MenuItem value="hours">Hours</MenuItem>
                                    <MenuItem value="days">Days</MenuItem>
                                    <MenuItem value="weeks">Weeks</MenuItem>
                                </Select>

                                <Select
                                    value={direction}
                                    size="small"
                                    onChange={e => setDirection(e.target.value as AdjustDirection)}
                                    fullWidth
                                >
                                    <MenuItem value="later">Later</MenuItem>
                                    <MenuItem value="earlier">Earlier</MenuItem>
                                </Select>
                            </Stack>
                        </Stack>
                    </Box>

                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedAutoRepostIds.length.toLocaleString()} Auto Reposts selected
                    </Typography>

                    {movedIntoPastCount > 0 && (
                        <Box sx={{ pl: 2, pr: 2 }}>
                            <Alert severity="warning">
                                {movedIntoPastCount.toLocaleString()} selected Auto Reposts would move into the past and will repost at the
                                next scheduler run.
                            </Alert>
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedAutoRepostIds.length > 0 &&
                                                    selectedAutoRepostIds.length < sortedAutoReposts.length
                                                }
                                                checked={
                                                    sortedAutoReposts.length > 0 &&
                                                    selectedAutoRepostIds.length === sortedAutoReposts.length
                                                }
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedAutoRepostIds(
                                                            sortedAutoReposts
                                                                .map(autoRepost => autoRepost._id)
                                                                .filter((id): id is string => Boolean(id)),
                                                        );
                                                    } else {
                                                        setSelectedAutoRepostIds([]);
                                                    }
                                                }}
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={sortLabelSx}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" width={50}>
                                            <TableSortLabel
                                                active={sortField === 'repostCount'}
                                                direction={sortField === 'repostCount' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('repostCount')}
                                                sx={sortLabelSx}
                                            >
                                                Reposts
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'nextRepostDate'}
                                                direction={sortField === 'nextRepostDate' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('nextRepostDate')}
                                                sx={sortLabelSx}
                                            >
                                                Next Repost
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>New Next Repost</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedAutoReposts.map(autoRepost => {
                                        const id = autoRepost._id || '';
                                        const isSelected = selectedAutoRepostIds.includes(id);

                                        return (
                                            <TableRow
                                                key={id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                    backgroundColor: theme =>
                                                        isSelected ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                                }}
                                            >
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedAutoRepostIds([...selectedAutoRepostIds, id]);
                                                            } else {
                                                                setSelectedAutoRepostIds(
                                                                    selectedAutoRepostIds.filter(selectedId => selectedId !== id),
                                                                );
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary" noWrap>
                                                        {autoRepost.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {autoRepost.repostCount.toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {moment(autoRepost.nextRepostDate).format(dateFormat)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        color={isSelected ? 'primary.main' : 'text.disabled'}
                                                        fontWeight={isSelected ? 'bold' : 'normal'}
                                                        noWrap
                                                    >
                                                        {shiftDate(autoRepost.nextRepostDate).format(dateFormat)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={bulkAdjustAutoRepostsLoading}
                    disabled={selectedAutoRepostIds.length === 0 || offsetAmount < 1}
                    onClick={() => {
                        handleAdjust();
                    }}
                >
                    <span>Adjust Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceAutoRepostBulkAdjustDialog;
